import { logger } from '@tunasong/models'
import type { AuthData } from '@tunasong/redux'

/**
 * Must equal `localUser.userId` in `backend/local-host/src/env.ts` — a FRESH data directory
 * seeds the local profile under this id (`bootstrap.ts`). It is the fallback only: a kernel
 * booted from an imported space adopts the exported user's id instead, and the SPA learns
 * which by asking the host (`fetchLocalSession`). Duplicated as a literal rather than
 * imported: the SPA does not depend on a backend package, and pulling one in for a string
 * is the wrong trade.
 */
export const LOCAL_USER_ID = 'local-user'
const LOCAL_EMAIL = 'local@tunasong.localhost'

/**
 * The far-future sentinel, in the two units the client reads it in: `exp` is JWT seconds,
 * `expires` is millis. Both are needed — `OAuth.isSessionExpired` reads ONLY
 * `idToken.payload.exp`, and `useCreateWebsocket` refuses to open the socket when it
 * reports expired. A session without it looks logged-in, serves entities over /api, and
 * silently never connects to /ws, so nothing typed is ever delivered or stored.
 */
const LOCAL_EXPIRY_SECONDS = 253_402_300_799

/**
 * Spec §16.4. Locked decision 10 makes the local host loopback-only with no auth, and
 * `authUser(event)` reads the userId the host stamps — never this token. The client still
 * needs the SHAPE of a session: `base-query.ts:114` asserts a non-empty token before every
 * request, and `AuthGuard` renders nothing until `isLoggedIn`.
 *
 * The token string is a deliberate sentinel. If VITE_TUNA_LOCAL ever leaks into a cloud
 * build, the app boots apparently-logged-in and every call 401s; this puts the cause in the
 * first log line rather than at the end of a debugging session.
 */
export const createLocalSession = ({ userId, email }: { userId: string; email: string }): AuthData => ({
  idToken: {
    jwtToken: 'local-kernel-no-auth',
    payload: { sub: userId, email, name: 'Local user', exp: LOCAL_EXPIRY_SECONDS },
  },
  accessToken: { jwtToken: 'local-kernel-no-auth' },
  expires: LOCAL_EXPIRY_SECONDS * 1000,
})

/** The session for a fresh data directory, and the fallback when the host cannot be asked */
export const localSession: AuthData = createLocalSession({ userId: LOCAL_USER_ID, email: LOCAL_EMAIL })

interface LocalSessionResponse {
  userId: string
  email: string
}

function isLocalSessionResponse(value: unknown): value is LocalSessionResponse {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const { userId, email } = value as Partial<LocalSessionResponse>
  return typeof userId === 'string' && userId.length > 0 && typeof email === 'string'
}

/**
 * Identity is data, not a build constant: the host stamps `kernel_meta.localUserId` on
 * every request, and the client must compare entity owners against THAT id — otherwise an
 * imported space reads as someone else's (disabled titles, no profile, blank settings).
 * `GET /api/session` is the one route the host answers itself (`server.ts`). Same origin
 * as the page, so the path is relative like the API base URL is.
 */
export const fetchLocalSession = async (): Promise<AuthData> => {
  try {
    const response = await fetch('/api/session', { headers: { accept: 'application/json' } })
    const body: unknown = await response.json()
    if (!response.ok || !isLocalSessionResponse(body)) {
      logger.warn(`Local host did not report a session (${response.status}); assuming ${LOCAL_USER_ID}`)
      return localSession
    }
    return createLocalSession(body)
  } catch (error) {
    logger.warn(`Could not ask the local host who the user is; assuming ${LOCAL_USER_ID}`, error)
    return localSession
  }
}
